import { useEffect, useMemo, useRef, useState, Dispatch, SetStateAction } from "react";
import { t } from "ttag";
import moment from "moment-timezone";
import { GlobalAdmin } from "coolremote-sdk";
import { useStoreActions } from "../../models/RootStore";
import {
  getFirmwareVersionId,
  isFirmwareUpdateFailed,
  normalizeFirmwareActionDetail,
} from "./firmwareUiUtils";

const DEFAULT_ESTIMATED_DURATION_SECONDS = 300;

const parseVersionParts = (version: string): number[] => {
  const nums = (version || "").match(/\d+/g);
  return nums ? nums.map(Number) : [];
};

const isNewerFirmwareVersion = (candidate: string, current: string): boolean => {
  if (!candidate || !current || candidate === current) {
    return false;
  }

  const candidateParts = parseVersionParts(candidate);
  const currentParts = parseVersionParts(current);

  for (let i = 0; i < Math.max(candidateParts.length, currentParts.length); i++) {
    const candidatePart = candidateParts[i] || 0;
    const currentPart = currentParts[i] || 0;
    if (candidatePart > currentPart) {
      return true;
    }
    if (candidatePart < currentPart) {
      return false;
    }
  }

  return false;
};

const COMPLETED_FW_STATUSES = new Set(["FINISHED", "IN_SYNC"]);
const ACTIVE_FW_STATUSES = new Set(["PENDING", "RUNNING", "UPDATING"]);

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const firmwareVersionMatches = (reported: any, expected: any): boolean => {
  if (!reported || !expected) {
    return false;
  }
  const normalize = (value: any) =>
    String(value)
      .trim()
      .toLowerCase()
      .replace(/^ng-v/, "")
      .replace(/^v/, "");
  const reportedNorm = normalize(reported);
  const expectedNorm = normalize(expected);
  return (
    reportedNorm === expectedNorm ||
    reportedNorm.startsWith(`${expectedNorm}-`) ||
    reportedNorm.startsWith(`${expectedNorm}.`)
  );
};

const unwrapApiData = (res: any) => res?.data?.data ?? res?.data ?? res ?? {};

const extractInstalledVersion = (installedRes: any): string => {
  const payload = unwrapApiData(installedRes);
  return payload?.version ? String(payload.version) : "";
};

const toDeviceStyleVersion = (catalogVersion: string, previousReported?: string) => {
  if (!catalogVersion) {
    return catalogVersion;
  }
  if (previousReported && /^ng-v/i.test(String(previousReported))) {
    return `ng-v${catalogVersion}`;
  }
  return catalogVersion;
};

const unwrapFirmwareStatus = (res: any) => {
  const outer = res?.data;
  const inner = outer?.data ?? outer ?? {};
  const status = String(inner?.status || "").toUpperCase();
  const fwStatus = String(inner?.fwStatus || "").toUpperCase();
  const forceType = inner?.forceType != null ? String(inner.forceType) : undefined;
  let canForce: boolean | undefined =
    typeof inner?.canForce === "boolean" ? inner.canForce : undefined;
  if (canForce === undefined && forceType) {
    canForce = forceType.toLowerCase() === "soft";
  }
  return {
    status,
    fwStatus,
    displayStatus: status || fwStatus,
    fwUpdateStage: String(inner?.fwUpdateStage || "").toUpperCase(),
    assignedVersion: inner?.assignedVersion,
    actionId: inner?.actionId,
    forceType,
    canForce,
    progressPercent:
      inner?.progressPercent != null ? Number(inner.progressPercent) : undefined,
    secondsRemaining:
      inner?.secondsRemaining != null ? Number(inner.secondsRemaining) : undefined,
  };
};

const isEdgeDevice = (deviceData: any) =>
  deviceData?.deviceType == 1 && deviceData?.type == 3;

export interface UseEdgeFirmwareOtaParams {
  device: any;
  setDevice: Dispatch<SetStateAction<any>>;
  deviceId: string;
  firmwareVersion: any;
  isNGdevice: boolean;
}

export function useEdgeFirmwareOta({
  device,
  setDevice,
  deviceId,
  firmwareVersion,
  isNGdevice,
}: UseEdgeFirmwareOtaParams) {
  const firmwareUpdateByVersion = useStoreActions(
    (actions) => actions.devices.firmwareUpdateByVersion
  );
  const cancelFirmwareUpdate = useStoreActions(
    (actions) => actions.devices.cancelFirmwareUpdate
  );
  const getFirmwareInstalled = useStoreActions(
    (actions) => actions.devices.getFirmwareInstalled
  );
  const getFirmwareActions = useStoreActions(
    (actions) => actions.devices.getFirmwareActions
  );
  const getFirmwareAction = useStoreActions(
    (actions) => actions.devices.getFirmwareAction
  );
  const forceFirmwareAction = useStoreActions(
    (actions) => actions.devices.forceFirmwareAction
  );
  const firmwareStatus = useStoreActions((actions) => actions.devices.firmwareStatus);
  const { addMessage } = useStoreActions((action) => action.errorMessage);

  const [availableFirmwareVersions, setAvailableFirmwareVersions] = useState<any[]>([]);
  const [selectedUpgradeVersion, setSelectedUpgradeVersion] = useState<any>(null);
  const [loadingFirmwareVersions, setLoadingFirmwareVersions] = useState<boolean>(false);
  const [openUpgradeDialog, setOpenUpgradeDialog] = useState<boolean>(false);
  const [isUpgradingFirmware, setIsUpgradingFirmware] = useState<boolean>(false);
  const [isFirmwareUpdating, setIsFirmwareUpdating] = useState<boolean>(false);
  const [firmwareLogs, setFirmwareLogs] = useState<string[]>([]);
  const [upgradeActionId, setUpgradeActionId] = useState<string | number | null>(null);
  const [isCancellingFirmware, setIsCancellingFirmware] = useState<boolean>(false);
  const [isForcingFirmware, setIsForcingFirmware] = useState<boolean>(false);
  const [canForceFirmware, setCanForceFirmware] = useState<boolean>(false);
  const [currentFwStatus, setCurrentFwStatus] = useState<string>("");
  const [currentFwStage, setCurrentFwStage] = useState<string>("");
  const [progressPercent, setProgressPercent] = useState<number>(0);
  const [secondsRemaining, setSecondsRemaining] = useState<number>(0);
  const [activeUpgradeVersionInfo, setActiveUpgradeVersionInfo] = useState<{
    version: string;
    isStable?: boolean;
  } | null>(null);
  const [upgradeJustCompleted, setUpgradeJustCompleted] = useState<boolean>(false);
  // The dialog can be closed while an update runs; polling carries on behind it.
  const [progressDialogHidden, setProgressDialogHidden] = useState<boolean>(false);
  const [upgradeFailed, setUpgradeFailed] = useState<boolean>(false);
  const [installedFirmwareVersion, setInstalledFirmwareVersion] = useState<string>("");
  const [openHistoryDialog, setOpenHistoryDialog] = useState<boolean>(false);
  const [firmwareActions, setFirmwareActions] = useState<any[]>([]);
  const [loadingFirmwareActions, setLoadingFirmwareActions] = useState<boolean>(false);
  const [selectedFirmwareAction, setSelectedFirmwareAction] = useState<any>(null);
  const [loadingFirmwareActionDetail, setLoadingFirmwareActionDetail] =
    useState<boolean>(false);

  const firmwarePollRef = useRef<any>(null);
  const firmwareClearLogsRef = useRef<any>(null);
  const lastFirmwareLogKeyRef = useRef<string>("");
  const firmwarePollingActiveRef = useRef<boolean>(false);
  const upgradeTargetVersionRef = useRef<string>("");
  const lastAssignedVersionRef = useRef<string>("");
  const installedFirmwareVersionRef = useRef<string>("");

  const appendFirmwareLog = (message: string) => {
    const stamp = moment().format("HH:mm:ss");
    setFirmwareLogs((prev) => [...prev, `[${stamp}] ${message}`]);
  };

  const stopFirmwarePolling = () => {
    firmwarePollingActiveRef.current = false;
    if (firmwarePollRef.current) {
      clearTimeout(firmwarePollRef.current);
      firmwarePollRef.current = null;
    }
  };

  const clearFirmwareLogsSoon = (delayMs = 4000) => {
    if (firmwareClearLogsRef.current) {
      clearTimeout(firmwareClearLogsRef.current);
    }
    firmwareClearLogsRef.current = setTimeout(() => {
      setFirmwareLogs([]);
      setIsFirmwareUpdating(false);
      setUpgradeActionId(null);
      setCanForceFirmware(false);
      setCurrentFwStage("");
      lastFirmwareLogKeyRef.current = "";
      firmwareClearLogsRef.current = null;
    }, delayMs);
  };

  const refreshDeviceAfterFirmwareUpdate = async ({
    previousVersion,
    expectedVersion,
    fetchEnrichedDevice,
  }: {
    previousVersion?: string;
    expectedVersion?: string;
    fetchEnrichedDevice: (
      options?: { skipStatusCheck?: boolean; expectedVersion?: string }
    ) => Promise<any>;
  }) => {
    try {
      appendFirmwareLog(t`Refreshing device information...`);

      let latest: any = await fetchEnrichedDevice({
        skipStatusCheck: true,
        expectedVersion,
      });
      const maxAttempts = 12;

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        const currentVersion = latest?.firmwareVersion || "";
        const installedVersion =
          latest?.installedVersion || installedFirmwareVersionRef.current;
        const caughtUp =
          (!!expectedVersion &&
            (firmwareVersionMatches(currentVersion, expectedVersion) ||
              firmwareVersionMatches(installedVersion, expectedVersion))) ||
          (!expectedVersion &&
            !!previousVersion &&
            !!currentVersion &&
            currentVersion !== previousVersion);

        if (caughtUp) {
          break;
        }

        appendFirmwareLog(
          `${t`Waiting for device to report new version`} (${attempt}/${maxAttempts})...`
        );
        await sleep(2000);
        latest = await fetchEnrichedDevice({
          skipStatusCheck: true,
          expectedVersion,
        });
      }

      const finalVersion = latest?.firmwareVersion || "-";
      const done =
        (expectedVersion && firmwareVersionMatches(finalVersion, expectedVersion)) ||
        (previousVersion && finalVersion !== previousVersion);

      appendFirmwareLog(
        done
          ? `${t`Device information updated`}: ${finalVersion}`
          : t`Device information refreshed (device may still report previous version briefly)`
      );

      if (done) {
        upgradeTargetVersionRef.current = "";
      }
    } catch (err: any) {
      appendFirmwareLog(err?.message || t`Failed to refresh device information`);
    }
  };

  const pollEdgeFirmwareStatus = async (
    deviceData: any,
    fetchEnrichedDevice: (
      options?: { skipStatusCheck?: boolean; expectedVersion?: string }
    ) => Promise<any>
  ) => {
    if (!firmwarePollingActiveRef.current) {
      return;
    }

    try {
      const statusRes = await firmwareStatus(deviceData.id);
      const payload = unwrapFirmwareStatus(statusRes);
      setCurrentFwStatus(payload.fwStatus || payload.status);
      setCurrentFwStage(payload.fwUpdateStage || payload.fwStatus || payload.status);
      if (payload.progressPercent != null) {
        setProgressPercent(payload.progressPercent);
      }
      if (payload.secondsRemaining != null) {
        setSecondsRemaining(payload.secondsRemaining);
      }
      if (payload.actionId != null) {
        setUpgradeActionId(payload.actionId);
      }
      if (typeof payload.canForce === "boolean") {
        setCanForceFirmware(payload.canForce);
      }
      const logKey = `${payload.displayStatus}|${payload.fwUpdateStage}|${payload.assignedVersion}`;

      if (logKey !== lastFirmwareLogKeyRef.current) {
        lastFirmwareLogKeyRef.current = logKey;
        if (payload.assignedVersion) {
          lastAssignedVersionRef.current = String(payload.assignedVersion);
          appendFirmwareLog(`${t`Assigned version`}: ${payload.assignedVersion}`);
        }
        if (payload.displayStatus) {
          appendFirmwareLog(`${t`Status`}: ${payload.displayStatus}`);
        }
        if (payload.fwUpdateStage) {
          appendFirmwareLog(`${t`Stage`}: ${payload.fwUpdateStage}`);
        }
      }

      const isComplete =
        COMPLETED_FW_STATUSES.has(payload.fwStatus) ||
        COMPLETED_FW_STATUSES.has(payload.status) ||
        COMPLETED_FW_STATUSES.has(payload.displayStatus);

      if (!isComplete && isFirmwareUpdateFailed(payload.fwStatus, payload.status)) {
        setIsFirmwareUpdating(false);
        setUpgradeFailed(true);
        setCanForceFirmware(false);
        stopFirmwarePolling();
        appendFirmwareLog(t`Firmware update failed`);
        return;
      }

      if (isComplete) {
        setIsFirmwareUpdating(false);
        setUpgradeJustCompleted(true);
        setCanForceFirmware(false);
        stopFirmwarePolling();
        setProgressPercent(100);
        setSecondsRemaining(0);
        const expectedVersion =
          upgradeTargetVersionRef.current ||
          lastAssignedVersionRef.current ||
          payload.assignedVersion ||
          "";
        await refreshDeviceAfterFirmwareUpdate({
          previousVersion: deviceData?.firmwareVersion,
          expectedVersion,
          fetchEnrichedDevice,
        });
        clearFirmwareLogsSoon(6000);
        return;
      }

      firmwarePollRef.current = setTimeout(() => {
        pollEdgeFirmwareStatus(deviceData, fetchEnrichedDevice);
      }, 3000);
    } catch (err: any) {
      appendFirmwareLog(err?.message || t`Failed to get firmware status`);
      firmwarePollRef.current = setTimeout(() => {
        pollEdgeFirmwareStatus(deviceData, fetchEnrichedDevice);
      }, 5000);
    }
  };

  const fetchEnrichedDeviceRef = useRef<
    (options?: { skipStatusCheck?: boolean; expectedVersion?: string }) => Promise<any>
  >(() => Promise.resolve(undefined));

  const enrichDevice = async (
    rawRes: any,
    options: { expectedVersion?: string } = {}
  ) => {
    const res: any = unwrapApiData(rawRes);
    let installedVersion = "";

    if (isEdgeDevice(res) && (res?.id || deviceId)) {
      try {
        installedVersion = extractInstalledVersion(
          await getFirmwareInstalled(res.id || deviceId)
        );
        setInstalledFirmwareVersion(installedVersion);
        installedFirmwareVersionRef.current = installedVersion;
      } catch {
        setInstalledFirmwareVersion("");
        installedFirmwareVersionRef.current = "";
      }
    } else {
      setInstalledFirmwareVersion("");
      installedFirmwareVersionRef.current = "";
    }

    const expected = options.expectedVersion || upgradeTargetVersionRef.current;
    const shouldUseInstalled =
      !!expected &&
      !!installedVersion &&
      firmwareVersionMatches(installedVersion, expected) &&
      !firmwareVersionMatches(res?.firmwareVersion, expected);

    const nextDevice = {
      ...res,
      id: res?.id || deviceId,
      firmwareVersion: shouldUseInstalled
        ? toDeviceStyleVersion(installedVersion, res?.firmwareVersion)
        : res?.firmwareVersion,
      buildDate: res?.buildDate,
    };

    setDevice(nextDevice);
    return { ...nextDevice, installedVersion };
  };

  const checkEdgeStatusOnEnter = async (deviceData: any): Promise<boolean> => {
    if (!isEdgeDevice(deviceData)) {
      return false;
    }

    const status = await firmwareStatus(deviceData.id);
    const payload = unwrapFirmwareStatus(status);
    const isActive =
      ACTIVE_FW_STATUSES.has(payload.status) ||
      ACTIVE_FW_STATUSES.has(payload.fwStatus) ||
      payload.status === "UPDATING";

    if (payload.fwStatus || isActive) {
      if (isActive) {
        setIsFirmwareUpdating(true);
        setProgressDialogHidden(false);
        setCurrentFwStatus(payload.fwStatus || payload.status);
        setCurrentFwStage(payload.fwUpdateStage || payload.fwStatus || payload.status);
        setProgressPercent(payload.progressPercent ?? 0);
        setSecondsRemaining(
          payload.secondsRemaining ?? DEFAULT_ESTIMATED_DURATION_SECONDS
        );
        if (payload.assignedVersion) {
          setActiveUpgradeVersionInfo({ version: String(payload.assignedVersion) });
        }
        if (deviceData?.fmuAction) {
          setUpgradeActionId(deviceData.fmuAction);
        }
        if (typeof payload.canForce === "boolean") {
          setCanForceFirmware(payload.canForce);
        } else if (payload.forceType) {
          setCanForceFirmware(String(payload.forceType).toLowerCase() === "soft");
        } else {
          setCanForceFirmware(true);
        }
        appendFirmwareLog(t`Firmware update in progress`);
        firmwarePollingActiveRef.current = true;
        pollEdgeFirmwareStatus(deviceData, fetchEnrichedDeviceRef.current);
      }
      return true;
    }

    return true;
  };

  const cleanup = () => {
    stopFirmwarePolling();
    if (firmwareClearLogsRef.current) {
      clearTimeout(firmwareClearLogsRef.current);
    }
  };

  const resetOnDeviceChange = () => {
    setSelectedUpgradeVersion(null);
    setFirmwareLogs([]);
    setIsFirmwareUpdating(false);
    setUpgradeActionId(null);
    setCurrentFwStatus("");
    setCurrentFwStage("");
    setInstalledFirmwareVersion("");
    installedFirmwareVersionRef.current = "";
    setOpenHistoryDialog(false);
    setFirmwareActions([]);
    setSelectedFirmwareAction(null);
    lastFirmwareLogKeyRef.current = "";
    stopFirmwarePolling();
    setProgressPercent(0);
    setSecondsRemaining(0);
    setActiveUpgradeVersionInfo(null);
    setUpgradeJustCompleted(false);
    setUpgradeFailed(false);
    setProgressDialogHidden(false);
  };

  const dismissFirmwareProgressDialog = () => {
    if (isFirmwareUpdating) {
      setProgressDialogHidden(true);
      return;
    }
    setProgressDialogHidden(false);
    setUpgradeFailed(false);
    setUpgradeJustCompleted(false);
    setActiveUpgradeVersionInfo(null);
    setProgressPercent(0);
    setSecondsRemaining(0);
  };

  useEffect(() => {
    resetOnDeviceChange();
  }, [deviceId]);

  useEffect(() => {
    if (!isNGdevice) {
      setAvailableFirmwareVersions([]);
      setLoadingFirmwareVersions(false);
      return;
    }

    setLoadingFirmwareVersions(true);
    GlobalAdmin.getFirmwareVersions(1, 100)
      .then((res: any) => {
        const { versions = [] } = res || {};
        setAvailableFirmwareVersions(versions);
      })
      .catch((err: any) => {
        addMessage({ message: err.message || t`Failed to load firmware versions` });
      })
      .finally(() => setLoadingFirmwareVersions(false));
  }, [device?.deviceType, device?.type, isNGdevice]);

  const closeUpgradeDialog = () => {
    if (isUpgradingFirmware) {
      return;
    }
    setOpenUpgradeDialog(false);
    setSelectedUpgradeVersion(null);
  };

  const handleUpgradeFirmware = async () => {
    if (!isNGdevice) {
      return;
    }
    const firmwareVersionId = getFirmwareVersionId(selectedUpgradeVersion);
    const targetDeviceId = device.id || deviceId;

    if (!firmwareVersionId) {
      addMessage({ message: t`Select a firmware version first` });
      return;
    }
    if (!targetDeviceId) {
      addMessage({ message: t`Device id is missing` });
      return;
    }
    if (isUpgradingFirmware) {
      return;
    }

    setIsUpgradingFirmware(true);
    try {
      const res: any = await firmwareUpdateByVersion({
        deviceId: targetDeviceId,
        firmwareVersionId,
      });
      const actionId = res?.actionId || res?.data?.actionId || null;
      const selectedVersionLabel = selectedUpgradeVersion?.version || firmwareVersionId;
      upgradeTargetVersionRef.current = selectedUpgradeVersion?.version
        ? String(selectedUpgradeVersion.version)
        : "";
      lastAssignedVersionRef.current = upgradeTargetVersionRef.current;
      setActiveUpgradeVersionInfo({
        version: selectedUpgradeVersion?.version || String(firmwareVersionId),
        isStable: selectedUpgradeVersion?.isStable,
      });
      setUpgradeJustCompleted(false);
      setUpgradeFailed(false);
      setProgressDialogHidden(false);
      setUpgradeActionId(actionId);
      setCanForceFirmware(true);
      setIsFirmwareUpdating(true);
      setFirmwareLogs([]);
      lastFirmwareLogKeyRef.current = "";
      appendFirmwareLog(`${t`Upgrade started`}: ${selectedVersionLabel}`);
      if (actionId) {
        appendFirmwareLog(`${t`Action ID`}: ${actionId}`);
      }
      setOpenUpgradeDialog(false);
      setSelectedUpgradeVersion(null);
      stopFirmwarePolling();
      firmwarePollingActiveRef.current = true;
      setProgressPercent(0);
      setSecondsRemaining(DEFAULT_ESTIMATED_DURATION_SECONDS);
      pollEdgeFirmwareStatus({ ...device, id: targetDeviceId }, fetchEnrichedDeviceRef.current);
    } catch (err: any) {
      addMessage({ message: err?.message || t`Firmware upgrade failed` });
    } finally {
      setIsUpgradingFirmware(false);
    }
  };

  const handleCancelFirmwareUpgrade = async () => {
    if (!device.id || isCancellingFirmware || !isNGdevice) {
      return;
    }

    setIsCancellingFirmware(true);
    try {
      await cancelFirmwareUpdate({
        deviceId: device.id,
        actionId: upgradeActionId || undefined,
      });
      appendFirmwareLog(t`Cancel requested`);
      setIsFirmwareUpdating(false);
      setCurrentFwStatus("");
      setCurrentFwStage("");
      setCanForceFirmware(false);
      stopFirmwarePolling();
      setProgressPercent(0);
      setSecondsRemaining(0);
      setActiveUpgradeVersionInfo(null);
      setUpgradeJustCompleted(false);
      clearFirmwareLogsSoon(3000);
    } catch (err: any) {
      addMessage({ message: err?.message || t`Failed to cancel firmware upgrade` });
      appendFirmwareLog(err?.message || t`Failed to cancel firmware upgrade`);
    } finally {
      setIsCancellingFirmware(false);
    }
  };

  const handleForceFirmwareUpgrade = async () => {
    const actionId = upgradeActionId || device?.fmuAction;
    if (!device.id || !actionId || isForcingFirmware || !isNGdevice) {
      return;
    }

    setIsForcingFirmware(true);
    try {
      await forceFirmwareAction({
        deviceId: device.id,
        actionId,
        forceType: "forced",
      });
      setCanForceFirmware(false);
      appendFirmwareLog(t`Force update requested`);
    } catch (err: any) {
      const alreadyForced =
        err?.status === 409 || /already forced/i.test(String(err?.message || ""));
      if (alreadyForced) {
        setCanForceFirmware(false);
      }
      addMessage({ message: err?.message || t`Failed to force firmware upgrade` });
      appendFirmwareLog(err?.message || t`Failed to force firmware upgrade`);
    } finally {
      setIsForcingFirmware(false);
    }
  };

  const openFirmwareHistory = async () => {
    if (!device.id || !isNGdevice) {
      return;
    }

    setOpenHistoryDialog(true);
    setSelectedFirmwareAction(null);
    setLoadingFirmwareActions(true);
    try {
      const res: any = await getFirmwareActions({
        deviceId: device.id,
        limit: 20,
        sort: "id:DESC",
      });
      let actions: any[] =
        res?.content ||
        res?.data?.content ||
        res?.data ||
        (Array.isArray(res) ? res : []);
      if (!Array.isArray(actions)) {
        actions = [];
      }

      const missingVersion = actions.filter(
        (action) =>
          action?.id &&
          !action?.distributionSetVersion &&
          !action?.distributionSetName
      );
      if (missingVersion.length && device.id) {
        const deviceIdForActions = device.id;
        const enrichedRows = await Promise.all(
          missingVersion.map(async (action) => {
            try {
              const detailRes: any = await getFirmwareAction({
                deviceId: deviceIdForActions,
                actionId: action.id,
              });
              const detail = normalizeFirmwareActionDetail(
                detailRes?.data || detailRes || {}
              );
              return {
                actionId: action.id,
                distributionSetVersion:
                  detail.summary?.distributionSetVersion ||
                  detail.catalogFirmware?.version ||
                  detail.distributionSet?.version ||
                  detail.distributionSet?.name ||
                  undefined,
                distributionSetName:
                  detail.summary?.distributionSetName ||
                  detail.distributionSet?.name ||
                  undefined,
                distributionSetId:
                  detail.summary?.distributionSetId ||
                  detail.distributionSet?.id ||
                  undefined,
              };
            } catch {
              return { actionId: action.id };
            }
          })
        );
        const byId = new Map(enrichedRows.map((row) => [String(row.actionId), row]));
        actions = actions.map((action) => {
          const row = byId.get(String(action.id));
          if (!row) {
            return action;
          }
          return {
            ...action,
            distributionSetVersion:
              action.distributionSetVersion || row.distributionSetVersion,
            distributionSetName:
              action.distributionSetName || row.distributionSetName,
            distributionSetId: action.distributionSetId || row.distributionSetId,
          };
        });
      }

      setFirmwareActions(actions);
    } catch (err: any) {
      addMessage({ message: err?.message || t`Failed to load firmware history` });
      setFirmwareActions([]);
    } finally {
      setLoadingFirmwareActions(false);
    }
  };

  const openFirmwareActionDetail = async (actionId: string | number) => {
    if (!device.id || !actionId) {
      return;
    }

    setLoadingFirmwareActionDetail(true);
    setSelectedFirmwareAction(null);
    try {
      const res: any = await getFirmwareAction({
        deviceId: device.id,
        actionId,
      });
      const payload = res?.data || res || null;
      setSelectedFirmwareAction(normalizeFirmwareActionDetail(payload));
    } catch (err: any) {
      addMessage({ message: err?.message || t`Failed to load action details` });
    } finally {
      setLoadingFirmwareActionDetail(false);
    }
  };

  const newerFirmwareVersions = useMemo(() => {
    const activeVer = installedFirmwareVersion || firmwareVersion;
    return (availableFirmwareVersions || [])
      .filter((item: any) => item?.version !== activeVer)
      .sort((a: any, b: any) => {
        if (a?.isStable === b?.isStable) {
          return String(b?.version || "").localeCompare(String(a?.version || ""), undefined, {
            numeric: true,
          });
        }
        return a?.isStable ? -1 : 1;
      });
  }, [availableFirmwareVersions, firmwareVersion, installedFirmwareVersion]);

  const currentFirmwareCatalog = useMemo(() => {
    const versions = availableFirmwareVersions || [];
    const findMatch = (reported: any) =>
      versions.find((item: any) => firmwareVersionMatches(reported, item?.version));
    return (
      findMatch(firmwareVersion) ||
      findMatch(installedFirmwareVersion) ||
      null
    );
  }, [availableFirmwareVersions, firmwareVersion, installedFirmwareVersion]);

  return {
    enrichDevice,
    checkEdgeStatusOnEnter,
    fetchEnrichedDeviceRef,
    cleanup,
    availableFirmwareVersions,
    currentFirmwareCatalog,
    selectedUpgradeVersion,
    setSelectedUpgradeVersion,
    loadingFirmwareVersions,
    openUpgradeDialog,
    setOpenUpgradeDialog,
    isUpgradingFirmware,
    isFirmwareUpdating,
    firmwareLogs,
    upgradeActionId,
    isCancellingFirmware,
    isForcingFirmware,
    canForceFirmware,
    currentFwStatus,
    currentFwStage,
    progressPercent,
    secondsRemaining,
    activeUpgradeVersionInfo,
    upgradeJustCompleted,
    progressDialogHidden,
    upgradeFailed,
    dismissFirmwareProgressDialog,
    installedFirmwareVersion,
    openHistoryDialog,
    setOpenHistoryDialog,
    firmwareActions,
    loadingFirmwareActions,
    selectedFirmwareAction,
    setSelectedFirmwareAction,
    loadingFirmwareActionDetail,
    closeUpgradeDialog,
    handleUpgradeFirmware,
    handleCancelFirmwareUpgrade,
    handleForceFirmwareUpgrade,
    openFirmwareHistory,
    openFirmwareActionDetail,
    newerFirmwareVersions,
  };
}
